import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getProducts, getCollections } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";

const SORTS = [
  { id: "", label: "Featured" },
  { id: "price-asc", label: "Price ↑" },
  { id: "price-desc", label: "Price ↓" },
];

export default function Shop() {
  const { handle = "new" } = useParams();
  const [sort, setSort] = useState("");
  const { data: allProducts = [], isLoading } = useQuery({
    queryKey: ["shop", handle, sort],
    queryFn: () => getProducts(handle === "all" ? { sort: sort || undefined } : { collection: handle, sort: sort || undefined }),
  });
  const products = allProducts.filter((p) => p.available);
  const { data: collections = [] } = useQuery({ queryKey: ["collections"], queryFn: getCollections });
  const meta = useMemo(
    () => (handle === "all" ? { title: "Shop All", subtitle: "Everything" } : collections.find((c) => c.handle === handle)),
    [collections, handle]
  );

  return (
    <div>
      <section className="relative bg-[#0A0A0A] text-[#F4F2ED] grain">
        <div className="relative z-10 px-5 sm:px-8 lg:px-12 py-16 sm:py-24">
          <p className="overline text-[#C6A15B]">{meta?.subtitle || "Collection"}</p>
          <h1 className="font-hero text-6xl sm:text-8xl mt-3 leading-none">{(meta?.title || handle).toUpperCase()}</h1>
          <p className="font-mono text-xs text-white/40 mt-4">{products.length} pieces</p>
        </div>
      </section>

      <div className="px-5 sm:px-8 lg:px-12 py-6 border-b border-black/10 flex items-center justify-between sticky top-16 lg:top-20 bg-[#F4F2ED]/90 backdrop-blur z-30">
        <span className="overline text-black/40">All Pieces</span>
        <div className="flex gap-4">
          {SORTS.map((s) => (
            <button key={s.id} data-testid={`sort-${s.id || "featured"}`} onClick={() => setSort(s.id)}
                    className={`overline ${sort === s.id ? "text-black" : "text-black/40"} hover:text-black transition-colors`}>{s.label}</button>
          ))}
        </div>
      </div>

      <section className="px-5 sm:px-8 lg:px-12 py-14">
        {isLoading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[3/4] bg-[#EAE8E2] animate-pulse" />)}
          </div>
        ) : products.length === 0 ? (
          <p className="font-body text-black/50 py-20 text-center">No pieces in this collection yet.</p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10">
            {products.map((p, i) => <ProductCard key={p.handle} product={p} index={i} />)}
          </div>
        )}
      </section>
    </div>
  );
}
